import React, { useState } from 'react';
import { Plus, Scissors } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { usePatternStudio } from '../context/PatternStudioContext';
import SectionCard from '../builder/SectionCard';

const PRESET_SECTIONS = {
  Bear:      ['Head', 'Left Ear', 'Right Ear', 'Body', 'Left Arm', 'Right Arm', 'Left Leg', 'Right Leg', 'Snout', 'Assembly'],
  Bunny:     ['Head', 'Left Ear', 'Right Ear', 'Body', 'Left Arm', 'Right Arm', 'Left Leg', 'Right Leg', 'Tail', 'Assembly'],
  Cat:       ['Head', 'Left Ear', 'Right Ear', 'Body', 'Left Arm', 'Right Arm', 'Left Leg', 'Right Leg', 'Tail', 'Assembly'],
  default:   ['Head', 'Body', 'Left Arm', 'Right Arm', 'Left Leg', 'Right Leg', 'Tail', 'Assembly'],
};

const QUICK_SECTIONS = ['Head', 'Body', 'Ears', 'Arms', 'Legs', 'Tail', 'Snout', 'Wings', 'Assembly', 'Custom'];

export default function Step4PatternBuilder() {
  const { state, dispatch } = usePatternStudio();
  const sections = state.pattern.sections;
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [customName, setCustomName] = useState('');

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    dispatch({ type: 'REORDER_SECTIONS', sections: arrayMove(sections, oldIdx, newIdx) });
  }

  function addSection(title) {
    dispatch({ type: 'ADD_SECTION', title });
    setShowQuickAdd(false);
    setCustomName('');
  }

  function loadPreset() {
    const presetKey = state.pattern.animal_type in PRESET_SECTIONS
      ? state.pattern.animal_type
      : 'default';
    const titles = PRESET_SECTIONS[presetKey];
    titles.forEach(title => dispatch({ type: 'ADD_SECTION', title }));
    setShowQuickAdd(false);
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Pattern Builder</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Build your pattern section by section. Drag to reorder.
          </p>
        </div>
        {sections.length > 0 && (
          <button
            onClick={() => setShowQuickAdd(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all active:scale-95 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
          >
            <Plus size={14} /> Add Section
          </button>
        )}
      </div>

      {/* Empty state */}
      {sections.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-stone-200 rounded-2xl bg-stone-50 mt-4">
          <Scissors size={36} className="mb-4 opacity-30" style={{ color: '#6B5744' }} />
          <p className="text-sm text-gray-500 mb-2 font-medium">No sections yet</p>
          <p className="text-xs text-gray-400 mb-6 text-center max-w-xs">
            A typical amigurumi has: Head · Body · Ears · Arms · Legs · Assembly
          </p>
          <div className="flex gap-3">
            <button
              onClick={loadPreset}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
            >
              Auto-generate Sections
            </button>
            <button
              onClick={() => setShowQuickAdd(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-stone-300 text-gray-700 text-sm hover:bg-stone-50"
            >
              <Plus size={14} /> Add Manually
            </button>
          </div>
        </div>
      )}

      {/* Quick-add section panel */}
      {showQuickAdd && (
        <div className="mt-4 p-4 bg-white border border-stone-200 rounded-2xl shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Add</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {QUICK_SECTIONS.filter(s => s !== 'Custom').map(title => (
              <button
                key={title}
                onClick={() => addSection(title)}
                className="px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-sm text-gray-700 hover:bg-stone-100 hover:border-stone-400 transition-all"
              >
                + {title}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={customName}
              onChange={e => setCustomName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && customName.trim() && addSection(customName.trim())}
              placeholder="Custom section name…"
              className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100"
            />
            <button
              onClick={() => customName.trim() && addSection(customName.trim())}
              disabled={!customName.trim()}
              className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all"
              style={{ background: '#6B5744' }}
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Section cards */}
      {sections.length > 0 && (
        <div className="mt-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {sections.map(section => (
                  <SectionCard key={section.id} section={section} />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add section at bottom */}
          {!showQuickAdd && (
            <button
              onClick={() => setShowQuickAdd(true)}
              className="mt-4 flex items-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-all w-full justify-center"
            >
              <Plus size={14} /> Add Another Section
            </button>
          )}
        </div>
      )}
    </div>
  );
}
